import React, { useEffect, useState } from "react";
import { Navigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n, LangSwitch } from "@/lib/i18n";
import { api, API } from "@/lib/api";
import { Fingerprint, ArrowRight, MicrosoftOutlookLogo } from "@phosphor-icons/react";

export default function LoginPage() {
  const { user, login, refresh } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [entraConfigured, setEntraConfigured] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/entra/status");
        setEntraConfigured(!!data.configured);
      } catch {}
    })();
    // Handle error from Entra redirect
    const err = searchParams.get("error");
    if (err) {
      const map = {
        domain_not_allowed: "Your email domain is not permitted to sign in.",
        account_disabled: "This account is disabled. Contact HR.",
        missing_state: "Session expired, please try again.",
        invalid_state: "Session expired, please try again.",
        exchange_failed: "Microsoft sign-in failed. Please try again.",
        no_email_claim: "Microsoft did not return an email for your account.",
      };
      setError(map[err] || `Sign-in error: ${err}`);
    }
    // Probably just returned from SSO: re-check session
    refresh();
    // eslint-disable-next-line
  }, []);

  if (user) return <Navigate to="/app/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  const loginWithMicrosoft = () => {
    window.location.href = `${API}/auth/entra/login`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden md:flex relative bg-black text-white flex-col p-10 justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-40"
             style={{ backgroundImage: "url('https://images.unsplash.com/photo-1759987383760-327efaf5522a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBicmlnaHQlMjBvZmZpY2UlMjBlbnRyYW5jZSUyMGdsYXNzfGVufDB8fHx8MTc3Njc3MTUwMXww&ixlib=rb-4.1.0&q=85')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <Fingerprint size={36} weight="bold" />
            <div className="font-display font-black text-3xl tracking-tighter">ATTENDIO</div>
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] mt-2 opacity-80">{t("brand_line")}</div>
        </div>

        <div className="relative">
          <div className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.9]">
            {t("manage_hr")}
          </div>
          <div className="mt-4 inline-block bg-[#FFCC00] text-black brut-border px-3 py-1 font-bold uppercase tracking-wider text-sm">
            v1.0 — {t("tagline")}
          </div>
        </div>

        <div className="relative flex items-center gap-2 text-xs font-mono opacity-80">
          <span className="w-2 h-2 bg-[#00CC33]" /> system online
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-4 md:p-8 bg-[#F4F4F0]">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-md brut-border bg-white p-6 md:p-8 brut-shadow-lg"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="font-display font-black text-3xl md:text-4xl tracking-tighter">{t("login")}</div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-black/60 mt-1">
                {t("login_hint")}
              </div>
            </div>
            <LangSwitch />
          </div>

          {/* Mode selector: PWA / DESKTOP / KIOSK */}
          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="login-mode-selector">
            <div className="brut-border bg-black text-white p-2 text-center" data-testid="mode-desktop">
              <div className="text-[9px] font-bold uppercase tracking-[0.15em] opacity-70">1 · {t("mode_desktop_label")}</div>
              <div className="font-display font-black text-lg tracking-tighter">DESKTOP</div>
            </div>
            <a href="/kiosk" className="brut-border bg-[#FFCC00] text-black p-2 text-center brut-hover" data-testid="mode-kiosk">
              <div className="text-[9px] font-bold uppercase tracking-[0.15em] opacity-70">2 · {t("mode_kiosk_label")}</div>
              <div className="font-display font-black text-lg tracking-tighter">KIOSK</div>
            </a>
            <a href="/kiosk" className="brut-border bg-[#FF66B2] text-black p-2 text-center brut-hover" data-testid="mode-pwa">
              <div className="text-[9px] font-bold uppercase tracking-[0.15em] opacity-70">3 · {t("mode_pwa_label")}</div>
              <div className="font-display font-black text-lg tracking-tighter">PWA</div>
            </a>
          </div>

          <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("email")}</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full brut-border p-3 mb-4 focus:outline-none focus:border-[#0047FF] focus:shadow-[4px_4px_0_0_#0047FF] transition-all"
            required
          />

          <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("password")}</label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full brut-border p-3 mb-2 focus:outline-none focus:border-[#0047FF] focus:shadow-[4px_4px_0_0_#0047FF] transition-all"
            required
          />

          {error && (
            <div
              data-testid="login-error"
              className="brut-border bg-[#FF3333] text-white p-2 text-sm font-bold mb-2"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full mt-4 bg-black text-white brut-border brut-shadow brut-hover p-4 font-display font-black text-xl tracking-tighter uppercase flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? "…" : t("sign_in")} <ArrowRight size={20} weight="bold" />
          </button>

          <div className="mt-3 text-right">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-xs font-bold uppercase tracking-wider underline hover:no-underline"
            >
              {t("forgot_password")}
            </Link>
          </div>

          {entraConfigured && (
            <>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-[2px] bg-black" />
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/60">or</div>
                <div className="flex-1 h-[2px] bg-black" />
              </div>
              <button
                type="button"
                data-testid="entra-login"
                onClick={loginWithMicrosoft}
                className="w-full bg-[#0047FF] text-white brut-border brut-shadow brut-hover p-3 font-bold tracking-tight uppercase flex items-center justify-center gap-2"
              >
                <MicrosoftOutlookLogo size={20} weight="bold" /> Sign in with Microsoft
              </button>
            </>
          )}

          <div className="mt-6 text-xs font-mono text-black/60">
            Kiosk PWA: <a href="/kiosk" className="underline font-bold" data-testid="kiosk-link">/kiosk</a>
          </div>
        </form>
      </div>
    </div>
  );
}
